/* eslint-disable @typescript-eslint/no-unused-vars */

/* eslint-disable react/prop-types */
import React, { FC, useState } from "react";

import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useLocalStorage } from "react-use";
import { v1 as uuidv1 } from "uuid";
import ListItemText from "@mui/material/ListItemText";

import { LanguageIcon, RafterDownIcon } from "@/assets/icons";
import { StyledHeaderMenuButton } from "@/components/layouts/styled";
import { Language } from "@/i18n";

import { StyledLanguageMenuItem, StyledLanguagePopover } from "./styled";

type LanguageItem = { key: Language; translation: string };

const LanguageSelector: FC = () => {
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [, saveStoredLanguage] = useLocalStorage<string>("language", "", {
        raw: true,
    });
    const menuID = uuidv1();
    const onClickLanguage = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onChangeLanguage = (theLanguage: string) => {
        handleClose();
        setTimeout(() => {
            queryClient.clear();
            i18n.changeLanguage(theLanguage);
            saveStoredLanguage(theLanguage);
        }, 300);
    };

    const availableLanguages: LanguageItem[] = [
        { key: Language.Dutch, translation: t("LanguageSelector.nl") },
        { key: Language.English, translation: t("LanguageSelector.en") },
        { key: Language.French, translation: t("LanguageSelector.fr") },
        { key: Language.German, translation: t("LanguageSelector.de") },
    ];

    return (
        <>
            <StyledHeaderMenuButton
                aria-haspopup="true"
                aria-owns={menuID}
                onClick={onClickLanguage}
                $active={!!anchorEl}
                title={t("Common.Language")}
                data-testid="language_selector-button-menu"
            >
                <div className={clsx("icon", anchorEl && "active")}>
                    <LanguageIcon />
                </div>
                <div className={clsx("text", anchorEl && "active")}>
                    {i18n.language.toUpperCase()}
                    <RafterDownIcon className="arrowIcon" style={!anchorEl ? null : { transform: "rotate(180deg)" }} />
                </div>
            </StyledHeaderMenuButton>
            <StyledLanguagePopover
                classes={{ paper: "popover-paper" }}
                id={menuID}
                keepMounted
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                {availableLanguages
                    .filter(({ key }) => key !== i18n.language)
                    .map(({ key, translation }) => (
                        <StyledLanguageMenuItem
                            key={`language_selector-option-${key}`}
                            data-testid={`language_selector-option-${key}`}
                            classes={{ root: "listitem-root" }}
                            onClick={() => {
                                onChangeLanguage(key);
                            }}
                        >
                            <ListItemText primary={translation} />
                        </StyledLanguageMenuItem>
                    ))}
            </StyledLanguagePopover>
        </>
    );
};

export default LanguageSelector;
